import { z } from 'zod';

import { AtualizarMeDtoUfCrmv, type EnderecoResponse } from '@/api/generated/model';

/** O `<Select>` do Radix não aceita `value=""`. */
export const SEM_UF = 'nenhuma';

/** Fonte única: o enum gerado do contrato (mesma lista do CRMV). */
export const UFS = Object.values(AtualizarMeDtoUfCrmv);
type Uf = (typeof UFS)[number];

/** Espelha `enderecoSchema` do backend — limites iguais, CEP com 8 dígitos. */
export const enderecoFormSchema = z.object({
  cep: z
    .string()
    .trim()
    .refine((v) => v === '' || v.replace(/\D/g, '').length === 8, 'CEP deve ter 8 dígitos'),
  logradouro: z.string().trim().max(200),
  numero: z.string().trim().max(20),
  complemento: z.string().trim().max(100),
  bairro: z.string().trim().max(100),
  cidade: z.string().trim().max(100),
  uf: z.string(),
});

export type EnderecoForm = z.infer<typeof enderecoFormSchema>;

export function enderecoParaFormulario(endereco: EnderecoResponse | null | undefined): EnderecoForm {
  return {
    cep: endereco?.cep ? endereco.cep.replace(/^(\d{5})(\d{3})$/, '$1-$2') : '',
    logradouro: endereco?.logradouro ?? '',
    numero: endereco?.numero ?? '',
    complemento: endereco?.complemento ?? '',
    bairro: endereco?.bairro ?? '',
    cidade: endereco?.cidade ?? '',
    uf: endereco?.uf && (UFS as readonly string[]).includes(endereco.uf) ? endereco.uf : SEM_UF,
  };
}

/**
 * Formulário → corpo da API. Tudo vazio vira `null` (apaga o endereço): o
 * formulário mostra o que está gravado, então o que a pessoa vê é o que envia.
 */
export function enderecoParaEnvio(form: EnderecoForm) {
  const opcional = (v: string) => (v.trim() ? v.trim() : null);
  const endereco = {
    cep: opcional(form.cep.replace(/\D/g, '')),
    logradouro: opcional(form.logradouro),
    numero: opcional(form.numero),
    complemento: opcional(form.complemento),
    bairro: opcional(form.bairro),
    cidade: opcional(form.cidade),
    uf: form.uf === SEM_UF ? null : (form.uf as Uf),
  };
  return Object.values(endereco).some((v) => v !== null) ? endereco : null;
}

/** O mínimo que o receituário de controle especial exige (mesma regra do backend). */
export function enderecoCompleto(endereco: EnderecoResponse | null | undefined): boolean {
  return Boolean(endereco?.logradouro && endereco.numero && endereco.cidade && endereco.uf);
}

/** Mesma linha que o PDF imprime ("Rua X, 12 — Bairro — Cidade/UF — CEP 00000-000"). */
export function formatarEndereco(endereco: EnderecoResponse | null | undefined): string | null {
  if (!endereco) return null;
  const rua = [endereco.logradouro, endereco.numero, endereco.complemento].filter(Boolean).join(', ');
  const cidade = [endereco.cidade, endereco.uf].filter(Boolean).join('/');
  const cep = endereco.cep ? `CEP ${endereco.cep.replace(/^(\d{5})(\d{3})$/, '$1-$2')}` : '';
  const partes = [rua, endereco.bairro, cidade, cep].filter(Boolean);
  return partes.length > 0 ? partes.join(' — ') : null;
}

/** "00000-000" enquanto digita. */
export function mascararCep(valor: string): string {
  const digitos = valor.replace(/\D/g, '').slice(0, 8);
  return digitos.length > 5 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : digitos;
}
